/**
 * oddit-collection-product-card
 * Shared card behaviors: image carousel (fade), swatches, quick-add,
 * lazy video loading. Works on any page where the card snippet is rendered.
 * Uses delegated events so it handles dynamically-appended cards (load more, AJAX).
 */
(function () {
  'use strict';

  var CARD_SEL = '.oddit-section-main-collection__product-card';
  var TRACK_SEL = '.oddit-section-main-collection__image-track';
  var BAR_SEL = '.oddit-section-main-collection__image-bar';
  var IMG_CONTAINER_SEL = '.oddit-section-main-collection__product-image';
  var PREV_SEL = '.oddit-section-main-collection__arrow--prev';
  var NEXT_SEL = '.oddit-section-main-collection__arrow--next';

  /* ======================================================================
     Hover state — JS-toggled `.is-hovered` instead of CSS `:hover`.
     Reason: when the cart drawer opens over a hovered card, the browser
     keeps the card in `:hover` state until the next mousemove, so the
     size overlay stays visible after the drawer closes. With a JS class
     we can clear it explicitly before opening the drawer.
     Uses delegated mouseover/mouseout so dynamically-added cards
     (load-more, AJAX) are handled without per-card init.
     ====================================================================== */
  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest(CARD_SEL);
    if (!card) return;
    if (!e.relatedTarget || !card.contains(e.relatedTarget)) {
      card.classList.add('is-hovered');
    }
  });

  document.addEventListener('mouseout', function (e) {
    var card = e.target.closest(CARD_SEL);
    if (!card) return;
    if (!e.relatedTarget || !card.contains(e.relatedTarget)) {
      card.classList.remove('is-hovered');
    }
  });

  /* ======================================================================
     Product description info — collection cards
     Desktop: hover/focus reveals. All devices: click/tap toggles and locks.
     Full description is already in server-rendered HTML; this JS only
     controls presentation and accessibility state.
     ====================================================================== */
  var DETAILS_SEL = '[data-product-details]';
  var DETAILS_TRIGGER_SEL = '[data-product-details-trigger]';
  // Prevent focus restoration after closing the mobile details dialog from
  // immediately re-opening it via the delegated focusin handler below.
  var suppressNextDetailsFocus = false;

  function setDetailsOpen(details, open, locked) {
    if (!details) return;
    var trigger = details.querySelector(DETAILS_TRIGGER_SEL);
    var card = details.closest(CARD_SEL);
    var panel = card ? card.querySelector('[data-product-details-panel]') : null;

    details.classList.toggle('is-open', !!open);
    if (locked === true) details.dataset.detailsLocked = 'true';
    if (locked === false) delete details.dataset.detailsLocked;
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (card) card.classList.toggle('is-details-open', !!open);
  }

  function closeOtherDetails(except) {
    var openDetails = document.querySelectorAll(DETAILS_SEL + '.is-open');
    for (var i = 0; i < openDetails.length; i++) {
      if (openDetails[i] !== except) setDetailsOpen(openDetails[i], false, false);
    }
  }

  document.addEventListener('click', function (e) {
    var closeButton = e.target.closest('[data-product-details-close]');
    if (closeButton) {
      e.preventDefault();
      e.stopPropagation();
      var closeCard = closeButton.closest(CARD_SEL);
      var closeDetails = closeCard ? closeCard.querySelector(DETAILS_SEL) : null;
      if (!closeDetails) return;
      var closeTrigger = closeDetails.querySelector(DETAILS_TRIGGER_SEL);
      setDetailsOpen(closeDetails, false, false);
      if (closeTrigger) {
        suppressNextDetailsFocus = true;
        closeTrigger.focus({ preventScroll: true });
        window.setTimeout(function () { suppressNextDetailsFocus = false; }, 0);
      }
      return;
    }

    var trigger = e.target.closest(DETAILS_TRIGGER_SEL);
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      var details = trigger.closest(DETAILS_SEL);
      if (!details) return;
      var willOpen = !details.classList.contains('is-open') || details.dataset.detailsLocked !== 'true';
      closeOtherDetails(details);
      setDetailsOpen(details, willOpen, willOpen);
      return;
    }

    // A click outside an open description dismisses it. Links and other
    // controls inside the description remain fully usable.
    if (!e.target.closest(DETAILS_SEL) && !e.target.closest('[data-product-details-panel]')) closeOtherDetails(null);
  });

  // Hover is enabled only on devices that genuinely support hover. This
  // prevents synthetic mobile hover states from making the panel sticky.
  document.addEventListener('mouseover', function (e) {
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;
    var details = e.target.closest(DETAILS_SEL);
    if (!details) return;
    if (e.relatedTarget && details.contains(e.relatedTarget)) return;
    closeOtherDetails(details);
    setDetailsOpen(details, true);
  });

  document.addEventListener('mouseout', function (e) {
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;

    var details = e.target.closest(DETAILS_SEL);
    if (details) {
      var card = details.closest(CARD_SEL);
      var panel = card ? card.querySelector('[data-product-details-panel]') : null;
      if (e.relatedTarget && (details.contains(e.relatedTarget) || (panel && panel.contains(e.relatedTarget)))) return;
      if (details.dataset.detailsLocked !== 'true' && !details.contains(document.activeElement) && !(panel && panel.contains(document.activeElement))) {
        setDetailsOpen(details, false);
      }
      return;
    }

    var hoveredPanel = e.target.closest('[data-product-details-panel]');
    if (hoveredPanel) {
      var panelCard = hoveredPanel.closest(CARD_SEL);
      var panelDetails = panelCard ? panelCard.querySelector(DETAILS_SEL) : null;
      if (!panelDetails) return;
      if (e.relatedTarget && panelCard && panelCard.contains(e.relatedTarget)) return;
      if (panelDetails.dataset.detailsLocked !== 'true' && !panelCard.contains(document.activeElement)) {
        setDetailsOpen(panelDetails, false);
      }
    }
  });

  document.addEventListener('focusin', function (e) {
    if (suppressNextDetailsFocus && e.target.closest(DETAILS_TRIGGER_SEL)) return;
    var details = e.target.closest(DETAILS_SEL);
    if (!details) return;
    closeOtherDetails(details);
    setDetailsOpen(details, true);
  });

  document.addEventListener('focusout', function (e) {
    var details = e.target.closest(DETAILS_SEL);
    var panel = e.target.closest('[data-product-details-panel]');
    var card = details ? details.closest(CARD_SEL) : (panel ? panel.closest(CARD_SEL) : null);
    if (!card) return;
    details = details || card.querySelector(DETAILS_SEL);
    if (!details) return;
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    if (details.dataset.detailsLocked !== 'true') setDetailsOpen(details, false);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var details = e.target.closest(DETAILS_SEL) || document.querySelector(DETAILS_SEL + '.is-open');
    if (!details || !details.classList.contains('is-open')) return;
    var trigger = details.querySelector(DETAILS_TRIGGER_SEL);
    setDetailsOpen(details, false, false);
    if (trigger) trigger.focus();
  });

  /* ======================================================================
     Swatch click — delegated globally
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('[data-swatch]');
    if (!swatch) return;
    var card = swatch.closest(CARD_SEL);
    if (!card) return;

    // Modifier-clicks (cmd/ctrl/shift, middle-click) should always navigate
    // natively so users can open the linked product in a new tab.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      return;
    }

    // Linked-product swatch represents a separate product. Swap the card
    // in-place (image, title, price, link, variants, add-to-bag) so it
    // feels like a single product with multiple colors.
    if (swatch.hasAttribute('data-linked-product')) {
      e.preventDefault();
      e.stopPropagation();
      applyLinkedSwatch(card, swatch);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    var swatches = card.querySelectorAll('[data-swatch]');
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.remove('oddit-section-main-collection__swatch--active');
    }
    swatch.classList.add('oddit-section-main-collection__swatch--active');

    // Update link with variant matching new color + current size
    var variants = getVariants(card);
    var newColor = swatch.getAttribute('data-color');
    var currentSize = getSelectedSize(card);
    var matchedVariant = variants ? findVariant(variants, newColor, currentSize) : null;
    if (matchedVariant) {
      updateCardLink(card, matchedVariant.id);
    } else {
      var variantUrl = swatch.dataset.variantUrl;
      if (variantUrl) {
        var links = card.querySelectorAll('a.oddit-section-main-collection__product-link');
        for (var j = 0; j < links.length; j++) {
          links[j].href = variantUrl;
        }
        var quickShopBtn = card.querySelector('[data-quick-view-trigger]');
        if (quickShopBtn) quickShopBtn.setAttribute('data-product-url', variantUrl);
      }
    }

    var variantMedia = parseSwatchMedia(swatch, 'data-variant-media');
    var imageBase = swatch.dataset.variantImage;
    if (variantMedia && variantMedia.length > 0) {
      rebuildTrack(card, variantMedia, swatch.getAttribute('aria-label') || swatch.dataset.color || null);
    } else if (imageBase) {
      var track = card.querySelector(TRACK_SEL);
      var primaryImg = track ? track.querySelector('img') : null;
      if (primaryImg) {
        var widths = [200, 300, 400, 600, 800];
        primaryImg.src = imageBase + '&width=400';
        primaryImg.srcset = widths
          .map(function (w) { return imageBase + '&width=' + w + ' ' + w + 'w'; })
          .join(', ');
      }
    }

    var imgContainer = card.querySelector(IMG_CONTAINER_SEL);
    if (imgContainer && imgContainer._goToSlide) {
      imgContainer._goToSlide(0);
    }

    updateSizeButtons(card);
  });

  /* ======================================================================
     Size button click — delegated globally
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.oddit-section-main-collection__size-btn');
    if (!btn || btn.disabled) return;
    var card = btn.closest(CARD_SEL);
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    var siblings = card.querySelectorAll('.oddit-section-main-collection__size-btn');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('oddit-section-main-collection__size-btn--active');
    }
    btn.classList.add('oddit-section-main-collection__size-btn--active');

    var variants = getVariants(card);
    if (!variants) return;
    var color = getSelectedColor(card);
    var size = btn.getAttribute('data-size');
    var variant = findVariant(variants, color, size);
    var addBtn = card.querySelector('.oddit-section-main-collection__add-to-bag');
    if (variant && addBtn) addBtn.setAttribute('data-variant-id', variant.id);
    if (variant) updateCardLink(card, variant.id);
  });

  /* ======================================================================
     Quick View Modal — fetch condensed product template into a modal
     ====================================================================== */
  var quickViewPreviouslyFocused = null;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;

    var productUrl = trigger.getAttribute('data-product-url');
    if (!productUrl) return;

    var modal = document.querySelector('[data-qv-modal]');
    var body = modal ? modal.querySelector('[data-qv-modal-body]') : null;
    if (!modal || !body) return;

    // Open modal with loading state
    quickViewPreviouslyFocused = document.activeElement;
    body.innerHTML = '<div class="oddit-qv-modal__loading">Loading...</div>';
    modal.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    var qvClose = modal.querySelector('[data-qv-modal-close]');
    if (qvClose) qvClose.focus();

    // Close the cart drawer if open so modal is visible
    if (window.odditCartDrawer && typeof window.odditCartDrawer.close === 'function') {
      window.odditCartDrawer.close();
    }

    // Fetch the quick-view section
    var fetchUrl = productUrl + (productUrl.indexOf('?') === -1 ? '?' : '&') + 'section_id=oddit-section-quick-view';
    fetch(fetchUrl, { cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        body.innerHTML = html;
        initQuickViewInteractions(body);
      })
      .catch(function () {
        body.innerHTML = '<div class="oddit-qv-modal__loading">Failed to load. <a href="' + productUrl + '">View product</a></div>';
      });
  });

  // Close quick-view modal
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-qv-modal-close]')) {
      closeQuickViewModal();
    }
  });

  document.addEventListener('keydown', function (e) {
    var modal = document.querySelector('[data-qv-modal][aria-hidden="false"]');
    if (!modal) return;
    if (e.key === 'Escape') {
      closeQuickViewModal();
      return;
    }
    if (e.key === 'Tab') {
      var dialog = modal.querySelector('.oddit-qv-modal__dialog');
      var focusable = dialog ? dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') : [];
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  function closeQuickViewModal() {
    var modal = document.querySelector('[data-qv-modal]');
    if (modal) {
      modal.setAttribute('aria-hidden', 'true');
      unlockBodyScroll();
      if (quickViewPreviouslyFocused && document.contains(quickViewPreviouslyFocused)) quickViewPreviouslyFocused.focus();
      quickViewPreviouslyFocused = null;
    }
  }

  // iOS Safari ignores `body { overflow: hidden }` and lets the underlying
  // page scroll-bubble through fixed modals. The reliable cross-browser fix
  // is to fix the body in place at the current scroll position, then restore
  // both on close.
  var savedScrollY = 0;
  function lockBodyScroll() {
    if (document.body.dataset.qvScrollLocked === 'true') return;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.dataset.qvScrollLocked = 'true';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  function unlockBodyScroll() {
    if (document.body.dataset.qvScrollLocked !== 'true') return;
    document.body.dataset.qvScrollLocked = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, savedScrollY);
  }

  function initQuickViewInteractions(container) {
    var section = container.querySelector('[data-quick-view-section]');
    if (!section) return;

    var variants = [];
    var variantsEl = section.querySelector('[data-quick-view-variants]');
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) {}
    }

    var selectedOptions = {};

    // Gallery state
    var gallery = section.querySelector('[data-qv-gallery]');
    var slidesContainer = section.querySelector('[data-qv-slides]');
    var slides = slidesContainer ? slidesContainer.querySelectorAll('[data-qv-slide]') : [];
    var prevBtn = section.querySelector('[data-qv-gallery-prev]');
    var nextBtn = section.querySelector('[data-qv-gallery-next]');
    var dots = section.querySelectorAll('[data-qv-gallery-dot]');
    var currentSlide = 0;
    var totalSlides = slides.length || 1;

    function goToSlide(index) {
      currentSlide = Math.max(0, Math.min(index, totalSlides - 1));
      if (slidesContainer) {
        slidesContainer.style.transform = 'translateX(-' + (currentSlide * 100) + '%)';
      }
      if (prevBtn) prevBtn.disabled = currentSlide <= 0;
      if (nextBtn) nextBtn.disabled = currentSlide >= totalSlides - 1;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('oddit-quick-view__gallery-dot--active', i === currentSlide);
      });
    }

    function goToMediaId(mediaId) {
      if (!mediaId) return;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          goToSlide(i);
          return;
        }
      }
    }

    // Touch swipe on gallery
    if (gallery && totalSlides > 1) {
      var touchStartX = 0;
      var touchCurrentX = 0;
      var isSwiping = false;

      gallery.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        // Initialize current to the same X so a tap (no touchmove) computes
        // diff = 0 and does NOT register as a swipe. Without this, touchCurrentX
        // is still 0 from init/reset and a tap on a button gets treated as a
        // big leftward swipe, hijacking the click handler.
        touchCurrentX = touchStartX;
        isSwiping = true;
      }, { passive: true });

      gallery.addEventListener('touchmove', function (e) {
        if (!isSwiping) return;
        touchCurrentX = e.touches[0].clientX;
      }, { passive: true });

      gallery.addEventListener('touchend', function () {
        if (!isSwiping) return;
        isSwiping = false;
        var diff = touchCurrentX - touchStartX;
        if (Math.abs(diff) > 30) {
          if (diff < 0 && currentSlide < totalSlides - 1) goToSlide(currentSlide + 1);
          else if (diff > 0 && currentSlide > 0) goToSlide(currentSlide - 1);
        }
        touchStartX = 0;
        touchCurrentX = 0;
      });
    }

    // Initialize from active buttons
    section.querySelectorAll('.oddit-quick-view__swatch--active, .oddit-quick-view__size-btn--active').forEach(function (el) {
      var pos = el.getAttribute('data-qv-option-position');
      var val = el.getAttribute('data-qv-option-value');
      if (pos && val) selectedOptions[pos] = val;
    });

    function findVariant() {
      var keys = Object.keys(selectedOptions);
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < keys.length; j++) {
          var pos = parseInt(keys[j], 10);
          if ((v.options[pos - 1] || '').trim() !== (selectedOptions[keys[j]] || '').trim()) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    // On initial load, jump the gallery to the active variant's featured
    // image. Prefer `featured_slide_index` (precomputed in Liquid against
    // the rendered slide order); fall back to `featured_media_id` matching.
    function navToVariant(variant) {
      if (!variant) return false;
      if (typeof variant.featured_slide_index === 'number' && variant.featured_slide_index >= 0) {
        goToSlide(variant.featured_slide_index);
        return true;
      }
      if (variant.featured_media_id) {
        goToMediaId(variant.featured_media_id);
        return true;
      }
      return false;
    }
    function navigateToActiveVariantImage() {
      if (navToVariant(findVariant())) return;
      var colorSwatch = section.querySelector('.oddit-quick-view__swatch--active');
      if (!colorSwatch) return;
      var colorPos = parseInt(colorSwatch.getAttribute('data-qv-option-position'), 10);
      var colorVal = colorSwatch.getAttribute('data-qv-option-value');
      if (!colorPos || !colorVal) return;
      for (var iv = 0; iv < variants.length; iv++) {
        var vv = variants[iv];
        if ((vv.options[colorPos - 1] || '').trim() === (colorVal || '').trim()) {
          if (navToVariant(vv)) return;
        }
      }
    }
    requestAnimationFrame(navigateToActiveVariantImage);

    function updateUI() {
      var variant = findVariant();
      var atc = section.querySelector('[data-qv-atc]');
      if (atc) {
        if (variant && variant.available) {
          atc.disabled = false;
          atc.textContent = 'ADD TO BAG \u2014 ' + variant.price_formatted;
          atc.setAttribute('data-variant-id', variant.id);
        } else {
          atc.disabled = true;
          atc.textContent = 'SOLD OUT';
        }
      }
      var colorPos = null;
      var colorVal = null;
      var activeSwatch = section.querySelector('.oddit-quick-view__swatch--active');
      if (activeSwatch) {
        colorPos = parseInt(activeSwatch.getAttribute('data-qv-option-position'), 10);
        colorVal = activeSwatch.getAttribute('data-qv-option-value');
      }
      section.querySelectorAll('.oddit-quick-view__size-btn').forEach(function (btn) {
        var sizePos = parseInt(btn.getAttribute('data-qv-option-position'), 10);
        var sizeVal = btn.getAttribute('data-qv-option-value');
        var available = false;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var colorMatch = !colorPos || (v.options[colorPos - 1] || '').trim() === (colorVal || '').trim();
          var sizeMatch = (v.options[sizePos - 1] || '').trim() === (sizeVal || '').trim();
          if (colorMatch && sizeMatch && v.available) { available = true; break; }
        }
        btn.classList.toggle('oddit-quick-view__size-btn--unavailable', !available);
      });
    }

    // Color swatch click
    section.addEventListener('click', function (e) {
      var swatch = e.target.closest('.oddit-quick-view__swatch');
      if (swatch) {
        section.querySelectorAll('.oddit-quick-view__swatch').forEach(function (s) {
          s.classList.remove('oddit-quick-view__swatch--active');
        });
        swatch.classList.add('oddit-quick-view__swatch--active');
        var pos = swatch.getAttribute('data-qv-option-position');
        var val = swatch.getAttribute('data-qv-option-value');
        if (pos && val) selectedOptions[pos] = val;
        var colorLabel = section.querySelector('[data-qv-color-value]');
        if (colorLabel) colorLabel.textContent = val;

        // Navigate gallery to the variant's featured image. Same helper as
        // the init path — prefers featured_slide_index, falls back to
        // featured_media_id matching.
        if (!navToVariant(findVariant())) {
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i];
            var colorPos = parseInt(pos, 10);
            if ((v.options[colorPos - 1] || '').trim() === (val || '').trim()) {
              if (navToVariant(v)) break;
            }
          }
        }

        updateUI();
        return;
      }

      // Size click
      var sizeBtn = e.target.closest('.oddit-quick-view__size-btn');
      if (sizeBtn) {
        section.querySelectorAll('.oddit-quick-view__size-btn').forEach(function (s) {
          s.classList.remove('oddit-quick-view__size-btn--active');
        });
        sizeBtn.classList.add('oddit-quick-view__size-btn--active');
        var pos = sizeBtn.getAttribute('data-qv-option-position');
        var val = sizeBtn.getAttribute('data-qv-option-value');
        if (pos && val) selectedOptions[pos] = val;
        updateUI();
        return;
      }

      // Gallery nav clicks
      if (e.target.closest('[data-qv-gallery-prev]')) {
        goToSlide(currentSlide - 1);
        return;
      }
      if (e.target.closest('[data-qv-gallery-next]')) {
        goToSlide(currentSlide + 1);
        return;
      }
      var dot = e.target.closest('[data-qv-gallery-dot]');
      if (dot) {
        goToSlide(parseInt(dot.getAttribute('data-qv-gallery-dot'), 10));
        return;
      }

      // ATC click
      var atcBtn = e.target.closest('[data-qv-atc]');
      if (atcBtn && !atcBtn.disabled) {
        var variantId = atcBtn.getAttribute('data-variant-id');
        if (!variantId) return;
        var origText = atcBtn.textContent;
        atcBtn.disabled = true;
        atcBtn.textContent = 'ADDING...';
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        })
        .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
        .then(function () { return fetch(rootUrl + 'cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          if (window.odditCartDrawer && window.odditCartDrawer.refresh) {
            return window.odditCartDrawer.refresh();
          }
        })
        .then(function () {
          closeQuickViewModal();
          if (window.odditCartDrawer && window.odditCartDrawer.open) {
            window.odditCartDrawer.open();
          }
        })
        .catch(function () {
          atcBtn.disabled = false;
          atcBtn.textContent = origText;
        });
      }
    });

    goToSlide(0);
    updateUI();
  }

  /* ======================================================================
     Add to bag click — delegated globally
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('.oddit-section-main-collection__add-to-bag');
    if (!addBtn) return;

    e.preventDefault();
    e.stopPropagation();

    var variantId = addBtn.getAttribute('data-variant-id');
    if (!variantId || addBtn.classList.contains('is-loading')) return;

    addBtn.classList.add('is-loading');
    var originalText = addBtn.textContent;
    addBtn.textContent = 'ADDING...';

    fetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then(function () {
        return fetch((window.Shopify.routes.root || '/') + 'cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        if (window.odditCartDrawer && typeof window.odditCartDrawer.refresh === 'function') {
          return window.odditCartDrawer.refresh();
        }
      })
      .then(function () {
        // Clear hover state and drop focus so the size overlay closes before
        // the drawer takes over — otherwise sticky-hover keeps it visible
        // after the user dismisses the drawer.
        var card = addBtn.closest(CARD_SEL);
        if (card) card.classList.remove('is-hovered');
        addBtn.blur();
        if (window.odditCartDrawer && typeof window.odditCartDrawer.open === 'function') {
          window.odditCartDrawer.open();
        }
      })
      .then(function () {
        addBtn.classList.remove('is-loading');
        addBtn.textContent = originalText;
      })
      .catch(function () {
        addBtn.classList.remove('is-loading');
        addBtn.textContent = 'ERROR';
        setTimeout(function () { addBtn.textContent = originalText; }, 2000);
      });
  });

  /* ======================================================================
     Linked-product swatch — swaps card content in place
     ====================================================================== */
  function applyLinkedSwatch(card, swatch) {
    // Mark active
    var swatches = card.querySelectorAll('[data-swatch]');
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.remove('oddit-section-main-collection__swatch--active');
    }
    swatch.classList.add('oddit-section-main-collection__swatch--active');

    var newUrl = swatch.dataset.variantUrl;
    var newTitle = swatch.dataset.linkedTitle;
    var newPrice = swatch.dataset.linkedPrice;
    var newCompare = swatch.dataset.linkedComparePrice;
    var onSale = swatch.dataset.linkedOnSale === '1';
    var imageBase = swatch.dataset.linkedImage || swatch.dataset.variantImage;
    var newProductId = swatch.dataset.linkedProductId;
    var newDefaultVariantId = swatch.dataset.linkedDefaultVariantId;
    var newDescription = swatch.dataset.linkedDescription;

    // Card root + product link href(s)
    if (newUrl) {
      card.setAttribute('data-product-base-url', newUrl);
      var links = card.querySelectorAll('a.oddit-section-main-collection__product-link');
      for (var j = 0; j < links.length; j++) {
        links[j].href = newUrl;
        if (newTitle) links[j].setAttribute('aria-label', newTitle);
      }
      var quickShopBtn = card.querySelector('[data-quick-view-trigger]');
      if (quickShopBtn) quickShopBtn.setAttribute('data-product-url', newUrl);
    }

    // Media track — full rebuild from data-linked-media when present so the
    // hover image and remaining slides reflect the linked product, not just
    // the featured image. Falls back to a single primary-image swap if the
    // attribute is missing/empty (graceful degradation).
    var mediaList = parseSwatchMedia(swatch, 'data-linked-media');
    if (mediaList && mediaList.length > 0) {
      rebuildTrack(card, mediaList, newTitle);
    } else if (imageBase) {
      var fallbackTrack = card.querySelector(TRACK_SEL);
      var primaryImg = fallbackTrack ? fallbackTrack.querySelector('img') : null;
      if (primaryImg) {
        var fallbackWidths = [200, 300, 400, 600, 800];
        primaryImg.src = imageBase + '&width=400';
        primaryImg.srcset = fallbackWidths
          .map(function (w) { return imageBase + '&width=' + w + ' ' + w + 'w'; })
          .join(', ');
        primaryImg.removeAttribute('alt');
      }
    }

    // Title — prefer display title (color stripped, mirrors the snippet
    // that the initial render uses) so the visible h3 stays consistent
    // with #ELA-146. Fall back to the full title for backward compat.
    var newDisplayTitle = swatch.dataset.linkedDisplayTitle || newTitle;
    if (newDisplayTitle) {
      var titleEl = card.querySelector('.oddit-section-main-collection__product-title');
      if (titleEl) titleEl.textContent = newDisplayTitle;
    }

    // Product-description panel follows linked-product swatches too.
    var details = card.querySelector(DETAILS_SEL);
    if (details && typeof newDescription === 'string') {
      var detailsContent = card.querySelector('[data-product-details-content]');
      var detailsTitle = card.querySelector('.oddit-section-main-collection__description-title');
      var detailsTrigger = details.querySelector(DETAILS_TRIGGER_SEL);
      if (detailsContent) detailsContent.innerHTML = newDescription;
      if (detailsTitle && newDisplayTitle) detailsTitle.textContent = newDisplayTitle;
      if (detailsTrigger && newTitle) detailsTrigger.setAttribute('aria-label', 'Product details for ' + newTitle);
      details.hidden = newDescription.trim() === '';
      setDetailsOpen(details, false, false);
    }

    // Price block
    if (newPrice) {
      var priceEl = card.querySelector('.oddit-section-main-collection__product-price');
      if (priceEl) {
        if (onSale && newCompare) {
          priceEl.classList.add('oddit-section-main-collection__product-price--on-sale');
          priceEl.innerHTML =
            '<span class="oddit-section-main-collection__price-compare">' + newCompare + '</span>' +
            '<span class="oddit-section-main-collection__price-sale">' + newPrice + '</span>';
        } else {
          priceEl.classList.remove('oddit-section-main-collection__product-price--on-sale');
          priceEl.innerHTML =
            '<span class="oddit-section-main-collection__price-regular">' + newPrice + '</span>';
        }
      }
    }

    // Sale/New badge — mirror the server-side badge-row logic (sale > new >
    // none). Without this, switching to a linked color-product that isn't
    // discounted leaves the original product's stale "SALE" badge on a
    // full-price color.
    var badgeRow = card.querySelector('.oddit-section-main-collection__badge-row');
    if (badgeRow) {
      var isNew = swatch.dataset.linkedIsNew === '1';
      var existingBadge = badgeRow.querySelector(
        '.oddit-section-main-collection__badge, .oddit-section-main-collection__badge-placeholder'
      );
      var newBadge = document.createElement('span');
      if (onSale) {
        newBadge.className = 'oddit-section-main-collection__badge oddit-section-main-collection__badge--sale';
        newBadge.textContent = 'SALE';
      } else if (isNew) {
        newBadge.className = 'oddit-section-main-collection__badge oddit-section-main-collection__badge--new';
        newBadge.textContent = 'NEW';
      } else {
        newBadge.className = 'oddit-section-main-collection__badge-placeholder';
      }
      if (existingBadge) {
        badgeRow.replaceChild(newBadge, existingBadge);
      } else {
        badgeRow.insertBefore(newBadge, badgeRow.firstChild);
      }
    }

    // Wishlist button data
    var wishlist = card.querySelector('[data-wishlist-btn]');
    if (wishlist) {
      if (newProductId) wishlist.setAttribute('data-product-id', newProductId);
      if (newDefaultVariantId) wishlist.setAttribute('data-variant-id', newDefaultVariantId);
      if (newUrl) wishlist.setAttribute('data-product-url', newUrl);
      if (newTitle) wishlist.setAttribute('data-product-title', newTitle);
      if (imageBase) wishlist.setAttribute('data-product-image', imageBase + '&width=200');
      var amount = swatch.dataset.linkedPriceAmount;
      if (amount) wishlist.setAttribute('data-product-price', amount);
    }

    // Variants JSON swap so quick-add size buttons reflect the linked product
    var newVariantsAttr = swatch.getAttribute('data-linked-variants');
    if (newVariantsAttr) {
      var variantsScript = card.querySelector('[data-variants-json]');
      if (variantsScript) variantsScript.textContent = newVariantsAttr;
      try { card._variantsCache = JSON.parse(newVariantsAttr); } catch (err) { card._variantsCache = null; }
    }

    // Add-to-bag default variant
    var addBtn = card.querySelector('.oddit-section-main-collection__add-to-bag');
    if (addBtn && newDefaultVariantId) {
      addBtn.setAttribute('data-variant-id', newDefaultVariantId);
    }

    // Reset slider to first slide
    var imgContainer = card.querySelector(IMG_CONTAINER_SEL);
    if (imgContainer && imgContainer._goToSlide) imgContainer._goToSlide(0);

    // Recompute size button availability against the new variants
    updateSizeButtons(card);
  }

  /* ======================================================================
     Swatch media rebuild
     ----------------------------------------------------------------------
     Both swatch flavors carry a JSON media payload that the click handler
     uses to tear down and rebuild the track:
       - linked-product swatches use `data-linked-media` — the linked
         product's full media list.
       - color-variant swatches use `data-variant-media` — this color's
         tagged images plus untagged shared images, with other colors'
         tagged images filtered out by the Liquid.
     Rebuilding (rather than just swapping slide 0) is what stops hover-
     preview and arrow/swipe navigation from landing on slides that belong
     to another color/product. The JSON shape is identical between the
     two flavors, so a single parser handles both.
     ====================================================================== */
  var IMG_SIZES = '(min-width: 990px) 25vw, 50vw';
  var IMG_DEFAULT_WIDTH = 354;
  var IMG_SRCSET_WIDTHS = [200, 300, 400, 600, 800];

  function parseSwatchMedia(swatch, attr) {
    var raw = swatch.getAttribute(attr);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function buildSlide(item) {
    if (!item || !item.type) return null;
    if (item.type === 'image') {
      if (!item.src) return null;
      var img = document.createElement('img');
      img.className = 'oddit-section-main-collection__product-img';
      img.src = item.src + '&width=' + IMG_DEFAULT_WIDTH;
      img.srcset = IMG_SRCSET_WIDTHS
        .map(function (w) { return item.src + '&width=' + w + ' ' + w + 'w'; })
        .join(', ');
      img.sizes = IMG_SIZES;
      img.removeAttribute('alt');
      img.loading = 'lazy';
      img.setAttribute('fetchpriority', 'low');
      img.width = IMG_DEFAULT_WIDTH;
      img.height = IMG_DEFAULT_WIDTH;
      return img;
    }
    if (item.type === 'video') {
      var vDiv = document.createElement('div');
      vDiv.className = 'oddit-section-main-collection__product-video';
      vDiv.setAttribute('data-lazy-video', '');
      if (item.poster) vDiv.setAttribute('data-poster', item.poster);
      vDiv.setAttribute('data-sources', JSON.stringify(item.sources || []));
      if (item.poster) {
        vDiv.style.backgroundImage = "url('" + item.poster + "')";
        vDiv.style.backgroundSize = 'cover';
        vDiv.style.backgroundPosition = 'center';
      }
      return vDiv;
    }
    if (item.type === 'external_video') {
      if (!item.iframe_src) return null;
      var eDiv = document.createElement('div');
      eDiv.className = 'oddit-section-main-collection__product-video';
      eDiv.setAttribute('data-lazy-external-video', '');
      eDiv.setAttribute('data-video-url', item.iframe_src);
      if (item.poster) {
        eDiv.style.backgroundImage = "url('" + item.poster + "')";
        eDiv.style.backgroundSize = 'cover';
        eDiv.style.backgroundPosition = 'center';
      }
      return eDiv;
    }
    return null;
  }

  function rebuildTrack(card, mediaList, fallbackAlt) {
    var track = card.querySelector(TRACK_SEL);
    if (!track) return;



    // Slot-0 image→image reuse: update src/srcset/alt on the existing <img>
    // instead of replacing it, so the previously-decoded pixels stay on
    // screen until the new ones decode. Avoids a blank flash on the only
    // slide that's visible without user action (slides 1+ are opacity:0).
    var firstOld = track.firstElementChild;
    var firstNew = mediaList[0];
    var canReuseFirst =
      firstOld && firstOld.tagName === 'IMG' &&
      firstNew && firstNew.type === 'image' && firstNew.src;

    if (canReuseFirst) {
      firstOld.src = firstNew.src + '&width=' + IMG_DEFAULT_WIDTH;
      firstOld.srcset = IMG_SRCSET_WIDTHS
        .map(function (w) { return firstNew.src + '&width=' + w + ' ' + w + 'w'; })
        .join(', ');
      firstOld.removeAttribute('alt');
      while (firstOld.nextSibling) track.removeChild(firstOld.nextSibling);
      for (var i = 1; i < mediaList.length; i++) {
        var node = buildSlide(mediaList[i]);
        if (node) track.appendChild(node);
      }
    } else {
      while (track.firstChild) track.removeChild(track.firstChild);
      for (var j = 0; j < mediaList.length; j++) {
        var fullNode = buildSlide(mediaList[j]);
        if (fullNode) track.appendChild(fullNode);
      }
    }

    // Wire up lazy videos for newly-inserted placeholders.
    initLazyVideos(track);
  }

  /* ======================================================================
     Variant helpers
     ====================================================================== */
  function getVariants(card) {
    var script = card.querySelector('[data-variants-json]');
    if (!script) return null;
    if (!card._variantsCache) {
      try { card._variantsCache = JSON.parse(script.textContent); } catch (e) { card._variantsCache = []; }
    }
    return card._variantsCache;
  }

function getSelectedColor(card) {
  var active = card.querySelector('.oddit-section-main-collection__swatch--active');
  if (!active) return null;

  // Linked-product swatches represent separate products, so their color
  // should not be used as a variant-option filter.
  if (active.hasAttribute('data-linked-product')) return null;

  return active.getAttribute('data-color');
}

  function getSelectedSize(card) {
    var active = card.querySelector('.oddit-section-main-collection__size-btn--active');
    return active ? active.getAttribute('data-size') : null;
  }

  function findVariant(variants, color, size) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var matchColor = !color || v.options.indexOf(color) !== -1;
      var matchSize = !size || v.options.indexOf(size) !== -1;
      if (matchColor && matchSize) return v;
    }
    return null;
  }

  function updateCardLink(card, variantId) {
    var baseUrl = card.getAttribute('data-product-base-url');
    if (!baseUrl || !variantId) return;
    var url = baseUrl + '?variant=' + variantId;
    var links = card.querySelectorAll('a.oddit-section-main-collection__product-link');
    for (var i = 0; i < links.length; i++) { links[i].href = url; }
    // Keep the Quick Shop trigger pointing at the same variant so the QV
    // modal opens with the selected color (gallery + selectors reflect it).
    var quickShopBtn = card.querySelector('[data-quick-view-trigger]');
    if (quickShopBtn) quickShopBtn.setAttribute('data-product-url', url);
  }

function updateSizeButtons(card) {
  var variants = getVariants(card);
  if (!variants || !variants.length) return;

  var color = getSelectedColor(card);

  var sizeContainer = card.querySelector(
    '.oddit-section-main-collection__size-options'
  );

  if (!sizeContainer) return;

  var activeSwatch = card.querySelector(
    '.oddit-section-main-collection__swatch--active'
  );

  var isLinkedProduct =
    activeSwatch && activeSwatch.hasAttribute('data-linked-product');

  /*
   * Linked color swatches can point to separate products with different
   * size ranges. Rebuild the buttons from the selected product's variants.
   */
  if (isLinkedProduct) {
    var allowedSizes = [
      'XXS',
      'XS',
      'S',
      'M',
      'L',
      'XL',
      'XXL',
      'XXXL',
      'OS',
      'O/S',
      'One Size'
    ];

    var sizes = [];

    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];

      for (var j = 0; j < options.length; j++) {
        var option = options[j];

        if (
          allowedSizes.indexOf(option) !== -1 &&
          sizes.indexOf(option) === -1
        ) {
          sizes.push(option);
        }
      }
    }

    if (sizes.length) {
      sizeContainer.innerHTML = '';

      for (var s = 0; s < sizes.length; s++) {
        var button = document.createElement('button');

        button.type = 'button';
        button.className =
          'oddit-section-main-collection__size-btn';

        button.setAttribute('data-size', sizes[s]);
        button.textContent = sizes[s];

        sizeContainer.appendChild(button);
      }
    }
  }

  var buttons = card.querySelectorAll(
    '.oddit-section-main-collection__size-btn'
  );

  var addBtn = card.querySelector(
    '.oddit-section-main-collection__add-to-bag'
  );

  var hasActive = false;

  for (var k = 0; k < buttons.length; k++) {
    var size = buttons[k].getAttribute('data-size');
    var variant = findVariant(variants, color, size);
    var available = variant && variant.available;

    buttons[k].disabled = !available;

    if (!available) {
      buttons[k].classList.remove(
        'oddit-section-main-collection__size-btn--active'
      );
    }

    if (
      available &&
      buttons[k].classList.contains(
        'oddit-section-main-collection__size-btn--active'
      )
    ) {
      hasActive = true;

      if (addBtn) {
        addBtn.setAttribute('data-variant-id', variant.id);
      }

      updateCardLink(card, variant.id);
    }
  }

  /*
   * If the previously selected size does not exist or is unavailable in the
   * newly selected color, automatically select the first available size.
   */
  if (!hasActive) {
    for (var n = 0; n < buttons.length; n++) {
      if (!buttons[n].disabled) {
        buttons[n].classList.add(
          'oddit-section-main-collection__size-btn--active'
        );

        var selectedSize = buttons[n].getAttribute('data-size');

        var selectedVariant = findVariant(
          variants,
          color,
          selectedSize
        );

        if (selectedVariant) {
          if (addBtn) {
            addBtn.setAttribute(
              'data-variant-id',
              selectedVariant.id
            );
          }

          updateCardLink(card, selectedVariant.id);
        }

        break;
      }
    }
  }
}

  /* ======================================================================
     No image/link sync on initial render
     ----------------------------------------------------------------------
     The card is rendered correct at the source and the JS leaves it alone on
     load:
       - The image track is built from `product.media` (featured image first,
         same order as the PDP gallery), and we do NOT rebuild it on init. An
         earlier version replaced the track with the active color's
         `data-variant-media`, which flashed the featured image and then hid
         it, leaving it unreachable in the carousel (#ELA-156).
     Color-specific media + links are still swapped in on an actual swatch
     click (see the click handlers above).
     ====================================================================== */

  function playVideoFromStart(video) {
    try { video.currentTime = 0; } catch (e) {}
    var p = video.play();
    if (p && typeof p.then === 'function') {
      video._lastPlay = p;
      p.catch(function () {});
    }
  }

  function pauseAndReset(video) {
    var stop = function () {
      try { video.pause(); } catch (e) {}
      try { video.currentTime = 0; } catch (e) {}
    };
    var pending = video._lastPlay;
    if (pending && typeof pending.then === 'function') {
      pending.then(stop, stop);
    } else {
      stop();
    }
  }

  /* ======================================================================
     Image carousel (fade) — init on any card
     ====================================================================== */
  function initCarousels(root) {
    var containers = root.querySelectorAll(IMG_CONTAINER_SEL);

    containers.forEach(function (container) {
      if (container._carouselInit) return;
      container._carouselInit = true;

      var card = container.closest(CARD_SEL);

      var track = container.querySelector(TRACK_SEL);
      var bar = container.querySelector(BAR_SEL);
      if (!track) return;

      // Make .is-active the single source of truth for which slide shows.
      // The CSS first-child rule only paints slide 0 until this runs (pre-JS
      // fallback). Flag slide 0 active now and mark the track initialized so
      // the fallback switches off — otherwise the first image stays painted
      // under later slides and bleeds through them on swipe (ELA-173).
      track.classList.add('is-initialized');
      if (track.firstElementChild) track.firstElementChild.classList.add('is-active');

      // Homepage cards opt out of touch swipe + per-card arrows but keep
      // the desktop hover-preview path. Detected via data attribute on the card.
      var noSwipe = !!(card && card.hasAttribute('data-no-image-swipe'));

      var currentIndex = 0;
      var startX = 0;
      var startY = 0;
      var currentX = 0;
      var isDragging = false;
      var direction = null;

      // Slide count is read lazily so linked-product swatch swaps that
      // change the track's children are reflected without re-init.
      function getTotalSlides() { return track.children.length || 1; }
      function getSlides() { return track.children; }

      if (bar) bar.style.width = (100 / getTotalSlides()) + '%';

      function goToSlide(index) {
        var slides = getSlides();
        var total = slides.length || 1;
        currentIndex = Math.max(0, Math.min(index, total - 1));
        for (var i = 0; i < slides.length; i++) {
          if (i === currentIndex) {
            slides[i].classList.add('is-active');
            if (slides[i].tagName === 'VIDEO') playVideoFromStart(slides[i]);
          } else {
            slides[i].classList.remove('is-active');
            if (slides[i].tagName === 'VIDEO' && !slides[i]._keepPlaying) {
              pauseAndReset(slides[i]);
            }
          }
        }
        if (bar) {
          bar.style.width = (100 / total) + '%';
          bar.style.left = (currentIndex * (100 / total)) + '%';
        }
      }

      var prevBtn = container.querySelector(PREV_SEL);
      var nextBtn = container.querySelector(NEXT_SEL);

      function updateArrows() {
        var total = getTotalSlides();
        if (prevBtn) prevBtn.disabled = currentIndex <= 0;
        if (nextBtn) nextBtn.disabled = currentIndex >= total - 1;
      }
      updateArrows();

      if (prevBtn) {
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (currentIndex > 0) goToSlide(currentIndex - 1);
          updateArrows();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (currentIndex < getTotalSlides() - 1) goToSlide(currentIndex + 1);
          updateArrows();
        });
      }

      if (!noSwipe) {
        track.addEventListener('touchstart', function (e) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          currentX = startX;
          isDragging = true;
          direction = null;
        }, { passive: true });

        track.addEventListener('touchmove', function (e) {
          if (!isDragging) return;
          currentX = e.touches[0].clientX;
          if (direction === null) {
            var dx = Math.abs(currentX - startX);
            var dy = Math.abs(e.touches[0].clientY - startY);
            if (dx > 5 || dy > 5) {
              direction = dx >= dy ? 'horizontal' : 'vertical';
            }
          }
          if (direction === 'horizontal') {
            e.preventDefault();
          }
        }, { passive: false });

        track.addEventListener('touchend', function () {
          if (!isDragging) return;
          isDragging = false;
          var diff = currentX - startX;
          if (direction === 'horizontal' && Math.abs(diff) > 30) {
            if (diff < 0 && currentIndex < getTotalSlides() - 1) goToSlide(currentIndex + 1);
            else if (diff > 0 && currentIndex > 0) goToSlide(currentIndex - 1);
          }
          startX = 0;
          startY = 0;
          currentX = 0;
          direction = null;
          updateArrows();
        });
      }

      container._goToSlide = function (index) {
        goToSlide(index);
        updateArrows();
      };

      // Hover always advances to slide 1. The Liquid is responsible for
      // putting the right media there: if product.media[1] is a video,
      // it's spliced into the variant-filtered list at slot 1; otherwise
      // slot 1 is the 2nd variant-filtered image.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        container.addEventListener('mouseenter', function () {
          if (getTotalSlides() <= 1) return;
          if (currentIndex !== 0) { updateArrows(); return; }
          goToSlide(1);
          updateArrows();
        });
        container.addEventListener('mouseleave', function () {
          if (getTotalSlides() <= 1) return;
          goToSlide(0);
          updateArrows();
        });
      }
    });
  }

  /* ======================================================================
     Eager video loading
     -----------------------------------------------------------------------
     Earlier this used an IntersectionObserver to lazily upgrade
     `data-lazy-video` placeholders into <video> elements as they
     approached the viewport. The lazy path had two failure modes:
     placeholders never upgraded (observer never fired for hidden cards)
     and races between observer-firing and user-hover. For
     correctness over bandwidth, we now upgrade every placeholder
     immediately on init — the browser still defers network for
     off-screen videos via `preload="auto"` heuristics, and hover-play
     is guaranteed to find a real <video> element in the slide. Errors
     are logged so a bad data-sources payload surfaces visibly instead
     of silently leaving a poster-only div.
     ====================================================================== */
  function initLazyVideos(root) {
    var placeholders = root.querySelectorAll('[data-lazy-video]:not([data-defer-preview-video]), [data-lazy-external-video]');
    if (!placeholders.length) return;

    placeholders.forEach(function (el) {
      try {
        if (el.hasAttribute('data-lazy-external-video')) {
          var url = el.getAttribute('data-video-url');
          var iframe = document.createElement('iframe');
          iframe.className = el.className;
          iframe.src = url;
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('allow', 'autoplay; encrypted-media');
          iframe.setAttribute('allowfullscreen', '');
          iframe.style.border = 'none';
          if (el.classList.contains('is-active')) iframe.classList.add('is-active');
          el.parentNode.replaceChild(iframe, el);
          return;
        }
        var sourcesRaw = el.getAttribute('data-sources');
        var sources = JSON.parse(sourcesRaw);
        // Shopify often lists an HLS .m3u8 source FIRST and the MP4 second.
        // Chrome/Firefox don't natively play HLS — without an HLS.js shim
        // they'd need to skip the first source, but the resource-selection
        // algorithm for dynamically-created <source> children is unreliable
        // and frequently leaves the video stuck on the unplayable m3u8.
        // Easiest correct fix for the hover-preview use case: keep only
        // formats the browser can play, prefer MP4 (works everywhere),
        // and only fall back to HLS on Safari which handles it natively.
        var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');
        var playable = sources.filter(function (s) {
          if (!s || !s.src) return false;
          var t = (s.type || '').toLowerCase();
          if (t === 'application/x-mpegurl' || t === 'application/vnd.apple.mpegurl') {
            return isSafari;
          }
          return true;
        });
        if (!playable.length) playable = sources; // safety net
        var video = document.createElement('video');
        video.className = el.className;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'auto';
        var poster = el.getAttribute('data-poster');
        if (poster) video.poster = poster;
        // Copy the placeholder's inline background-image style onto the
        // <video> element so the poster image is visible even when the
        // video can't render a frame yet (no source loaded, autoplay
        // blocked, network slow). The poster attribute alone isn't
        // always painted — some browsers leave the element transparent
        // until the resource selection algorithm finds something
        // playable (#ELA-139).
        if (el.style && el.style.cssText) {
          video.style.cssText = el.style.cssText;
        } else if (poster) {
          video.style.backgroundImage = "url('" + poster + "')";
          video.style.backgroundSize = 'cover';
          video.style.backgroundPosition = 'center';
        }
        video.setAttribute('aria-hidden', 'true');
        playable.forEach(function (s) {
          var src = document.createElement('source');
          src.src = s.src;
          src.type = s.type;
          video.appendChild(src);
        });
        var startActive = el.classList.contains('is-active');
        if (startActive) video.classList.add('is-active');
        el.parentNode.replaceChild(video, el);
        // Re-run the resource-selection algorithm now that sources are
        // attached. Without this, the <video> may stay in NETWORK_EMPTY
        // and never actually fetch the MP4 — which manifests as "video
        // never appears on hover" (#ELA-139).
        try { video.load(); } catch (e) {}
        if (startActive) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
      } catch (e) {
        // Surface bad data-sources payloads so we can fix the Liquid
        // escaping instead of silently leaving a static poster.
        if (window.console && console.error) {
          console.error('[oddit] lazy-video upgrade failed', e, el);
        }
      }
    });
  }

  /* ======================================================================
     Deferred homepage preview videos
     ----------------------------------------------------------------------
     Product Grid + Collection Carousel keep second-media video URLs dormant
     until the individual card approaches the viewport. The static poster
     remains visible, so the product card looks unchanged before activation.
     ====================================================================== */
  function initDeferredPreviewVideos(root) {
    var placeholders = root.querySelectorAll('[data-defer-preview-video][data-lazy-video]');
    if (!placeholders.length) return;

    function activate(el) {
      if (!el || !el.hasAttribute('data-defer-preview-video')) return;
      el.removeAttribute('data-defer-preview-video');
      var parent = el.parentNode;
      initLazyVideos(parent || document);

      var video = parent ? parent.querySelector('video.oddit-section-main-collection__product-video') : null;
      if (video) {
        try {
          video.preload = 'auto';
          video.load();
          primeFirstFrame(video);
        } catch (e) {}
      }
    }

    if (typeof IntersectionObserver === 'undefined') {
      placeholders.forEach(function (el) {
        var card = el.closest(CARD_SEL);
        if (!card) return;
        var activateOnce = function () { activate(el); };
        card.addEventListener('mouseenter', activateOnce, { once: true });
        card.addEventListener('touchstart', activateOnce, { once: true, passive: true });
        card.addEventListener('focusin', activateOnce, { once: true });
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          activate(entry.target);
        });
      },
      { rootMargin: '250px 250px' }
    );

    placeholders.forEach(function (el) {
      io.observe(el);

      var card = el.closest(CARD_SEL);
      if (!card) return;

      var activateOnce = function () {
        try { io.unobserve(el); } catch (e) {}
        activate(el);
      };

      card.addEventListener('mouseenter', activateOnce, { once: true });
      card.addEventListener('touchstart', activateOnce, { once: true, passive: true });
      card.addEventListener('focusin', activateOnce, { once: true });
    });
  }

  /* ======================================================================
     Warm the preview videos before they're first revealed
     ----------------------------------------------------------------------
     The inline preview <video> ships with preload="metadata", so the FIRST
     time it's shown is what triggers the fetch + decode — the user sees the
     poster flash to the first frame and the clip stutter while it buffers;
     every reveal after that is smooth because the data is cached.

     To kill that first-reveal flash we warm the clip ahead of time by
     upgrading preload to "auto" and kicking load() as the card approaches
     the viewport, THEN priming the first frame (see primeFirstFrame) so the
     decoded frame 0 is already painted before any reveal.

     Both pointer types get warmed: desktop reveals the video on hover, and
     on touch devices the SAME card reveals it by swiping to slide 2.
     Mobile was previously excluded, so swiping to the video on mobile showed
     the poster then faded the frame in — desktop (warmed) did not. Warming
     touch devices too makes the swipe-to-video reveal instant, matching
     desktop. (Originally ELA — hover video flash/stutter.)
     ====================================================================== */

  // Force the browser to decode and paint frame 0 while the video is still
  // hidden (opacity:0). A muted play() drives the decode pipeline regardless
  // of visibility; we pause and reset the moment it starts so nothing plays,
  // but the decoded first frame stays painted — so the real hover-play has no
  // poster -> frame fade. Runs at most once per video.
  function primeFirstFrame(video) {
    if (video._primed) return;
    video._primed = true;
    var settle = function () {
      // If the user has already revealed this slide (hover/swipe reached it
      // while we were priming), leave it playing — don't yank it back.
      if (video.classList.contains('is-active')) return;
      try { video.pause(); } catch (e) {}
      try { video.currentTime = 0; } catch (e) {}
    };
    var prime = function () {
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(settle, function () {});
      } else {
        settle();
      }
    };
    // HAVE_CURRENT_DATA (2) means frame 0 is available to decode.
    if (video.readyState >= 2) {
      prime();
    } else {
      video.addEventListener('loadeddata', prime, { once: true });
    }
  }

  function warmPreviewVideos(root) {
    if (typeof IntersectionObserver === 'undefined') return;
    var videos = root.querySelectorAll(
      'video.oddit-section-main-collection__product-video[preload="metadata"]'
    );
    if (!videos.length) return;

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var video = entry.target;
          obs.unobserve(video);
          try {
            video.preload = 'auto';
            video.load();
            primeFirstFrame(video);
          } catch (e) {}
        });
      },
      // Start fetching a bit before the card is actually on screen.
      { rootMargin: '300px 0px' }
    );

    videos.forEach(function (video) {
      io.observe(video);
    });
  }

  /* ======================================================================
     Initialize all cards on the page
     ====================================================================== */
  function initAllCards() {
    initCarousels(document);
    initDeferredPreviewVideos(document);
    initLazyVideos(document);
    warmPreviewVideos(document);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCards);
  } else {
    initAllCards();
  }

  // Re-init when Shopify theme editor reloads a section
  document.addEventListener('shopify:section:load', function (e) {
    initCarousels(e.target);
    initDeferredPreviewVideos(e.target);
    initLazyVideos(e.target);
    warmPreviewVideos(e.target);
  });

  // Expose for external use (e.g. after AJAX load-more appends new cards)
  window.odditInitProductCards = function (root) {
    initCarousels(root || document);
    initLazyVideos(root || document);
    warmPreviewVideos(root || document);
  };
})();
